// ============================================================
// clientRecordDiff.ts — what "Update this case" would change (pure;
// clientRecordDiff.test.ts).
// The case keeps its own copy of the client's details; POST
// /api/cases/:id/sync-client copies these 29 fields from the live client
// record over it (backend caseController.ts syncClientSnapshot — the SAME
// list). This compares every one of them, so the banner, its count and the
// confirm list cover exactly what the update writes:
//   • blank on one side, filled on the other → a change (either way: the
//     update also blanks a case field the record has blank);
//   • null / undefined / '' are all "blank";
//   • not a change when only the FORMAT differs: surrounding whitespace, a
//     date of birth written differently (12/04/1985 vs 1985-04-12), or a
//     postcode's spacing / capitals (ls1 1aa vs LS1 1AA).
// ============================================================
import { textDateToIso, ukDate } from '../../utils/civilDate'

export const CLIENT_RECORD_FIELDS = [
  'title', 'forename', 'surname',
  'addressLine1', 'addressLine2', 'addressTown', 'addressCounty', 'addressPostcode',
  'dateOfBirth', 'hasNiNumber', 'niNumber',
  'mainContactNumber', 'emailAddress', 'hasEmailAddress',
  'employmentStatus', 'occupation',
  'gpName', 'gpAddressLine1', 'gpAddressLine2', 'gpAddressTown', 'gpAddressCounty', 'gpAddressPostcode', 'gpPhone',
  'hospitalName', 'hospitalAddressLine1', 'hospitalAddressLine2', 'hospitalAddressTown', 'hospitalAddressCounty', 'hospitalAddressPostcode', 'hospitalPhone',
] as const
export type ClientRecordField = typeof CLIENT_RECORD_FIELDS[number]

// Names in the banner + confirm (each field its own name, so the count and
// the list always agree).
export const CLIENT_RECORD_LABEL: Record<ClientRecordField, string> = {
  title: 'Title', forename: 'Forename', surname: 'Surname',
  addressLine1: 'Address line 1', addressLine2: 'Address line 2', addressTown: 'Town', addressCounty: 'County', addressPostcode: 'Postcode',
  dateOfBirth: 'Date of birth', hasNiNumber: 'Has an NI number', niNumber: 'NI number',
  mainContactNumber: 'Main contact number', emailAddress: 'Email address', hasEmailAddress: 'Has an email address',
  employmentStatus: 'Employment', occupation: 'Occupation',
  gpName: 'GP name', gpAddressLine1: 'GP address line 1', gpAddressLine2: 'GP address line 2', gpAddressTown: 'GP town', gpAddressCounty: 'GP county', gpAddressPostcode: 'GP postcode', gpPhone: 'GP phone',
  hospitalName: 'Hospital name', hospitalAddressLine1: 'Hospital address line 1', hospitalAddressLine2: 'Hospital address line 2', hospitalAddressTown: 'Hospital town', hospitalAddressCounty: 'Hospital county', hospitalAddressPostcode: 'Hospital postcode', hospitalPhone: 'Hospital phone',
}

const isPostcode = (f: ClientRecordField): boolean => f === 'addressPostcode' || f === 'gpAddressPostcode' || f === 'hospitalAddressPostcode'

// The value as compared: blank → ''; booleans → 'true'/'false'; text trimmed;
// a readable date of birth → its ISO day; a postcode without spaces, upper case.
export const comparable = (field: ClientRecordField, v: unknown): string => {
  if (v === null || v === undefined) return ''
  if (typeof v === 'boolean') return String(v)
  const s = String(v).trim()
  if (s === '') return ''
  if (field === 'dateOfBirth') return textDateToIso(s) ?? s
  if (isPostcode(field)) return s.replace(/\s+/g, '').toUpperCase()
  return s
}

// For people: "(blank)", Yes / No, a UK date.
export const displayValue = (field: ClientRecordField, v: unknown): string => {
  if (v === null || v === undefined || (typeof v === 'string' && v.trim() === '')) return '(blank)'
  if (field === 'hasNiNumber' || field === 'hasEmailAddress') return v === true ? 'Yes' : v === false ? 'No' : '(blank)'
  if (field === 'dateOfBirth' && typeof v === 'string') {
    const iso = textDateToIso(v.trim())
    return iso ? ukDate(iso) : v.trim()
  }
  if (field === 'employmentStatus' && typeof v === 'string') return v.charAt(0).toUpperCase() + v.slice(1).replace(/[-_]/g, ' ')
  return String(v).trim()
}

export interface ClientRecordChange {
  field: ClientRecordField
  label: string
  caseValue: string // display
  recordValue: string // display
}

// Every field "Update this case" would change, in page order. No record
// linked → nothing (the update isn't possible).
export const clientRecordChanges = (caseCopy: Record<string, unknown> | null | undefined, record: Record<string, unknown> | null | undefined): ClientRecordChange[] => {
  if (!caseCopy || !record) return []
  return CLIENT_RECORD_FIELDS
    .filter((f) => comparable(f, caseCopy[f]) !== comparable(f, record[f]))
    .map((f) => ({ field: f, label: CLIENT_RECORD_LABEL[f], caseValue: displayValue(f, caseCopy[f]), recordValue: displayValue(f, record[f]) }))
}
